/**
 * Erreur normalisée produite par les services du front (cf. `handleServiceCall`).
 *
 * ## Contrat des champs
 *
 * | champ              | origine                                    | montrable au client ? |
 * | ------------------ | ------------------------------------------ | --------------------- |
 * | `message`          | `detail.message` de l'API                  | selon l'API           |
 * | `description`      | `detail.description` de l'API              | **oui**               |
 * | `code`             | `detail.code` de l'API                     | non (branchement)     |
 * | `details`          | `detail.details` de l'API                  | non (charge utile)    |
 * | `context`          | `detail.context` de l'API                  | non (charge utile)    |
 * | `technicalMessage` | message d'axios / de l'exception d'origine | **jamais**            |
 *
 * `description` est l'explication **rédigée pour l'utilisateur** par l'API : elle
 * lui dit ce qui s'est passé et quoi faire. `technicalMessage` est son opposé —
 * « Request failed with status code 502 » — et n'existe que pour le diagnostic
 * (console, Sentry).
 *
 * Jusqu'à DEV-1088, `description` recevait le message d'axios : un composant qui
 * croyait afficher une explication métier montrait le jargon HTTP au client.
 * D'où la séparation des deux champs.
 */
export class ServiceError extends Error {
    public code: string
    /** Explication rendue par l'API, écrite pour l'utilisateur. Affichable tel quel. */
    public description: string | null
    /** Message technique de l'exception d'origine. Diagnostic uniquement, jamais affiché. */
    public technicalMessage: string | null
    public status: number | null
    public details: any
    /** Charge utile `detail.context` de l'API (ex. chiffres du refus `forfait_atteint`). */
    public context: unknown

    constructor(
        data: {
            message: string
            code: string
            description?: string
            technicalMessage?: string
            details?: any
            context?: unknown
        },
        status?: number
    ) {
        super(data.message)
        this.name = "ServiceError"
        this.message = data.message
        this.code = data.code
        this.description = data.description ?? null
        this.technicalMessage = data.technicalMessage ?? null
        this.status = status ?? null
        this.details = data.details
        this.context = data.context
    }
}

/**
 * Explication destinée au client, si l'API en a rendu une.
 *
 * À utiliser dans les `catch` des composants pour préférer le texte de l'API à
 * un libellé i18n maison — sans jamais risquer d'afficher `technicalMessage`.
 *
 * ```ts
 * catch (error) {
 *     messageErreur.value = getUserFacingDescription(error) ?? t("download_all_error")
 * }
 * ```
 *
 * @param error - Erreur capturée, de type quelconque
 * @returns La description métier, ou `null` si l'erreur n'en porte pas
 */
export function getUserFacingDescription(error: unknown): string | null {
    return error instanceof ServiceError ? (error.description ?? null) : null
}
